import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmailDto, EmailEntityType, MailTransportInfo } from '@tww/shared';
import { fmtDateTime, fmtNum, parseAddress, richToText } from '@tww/shared';
import { api, ApiRequestError, qs } from '../../api/client';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { Note, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { Select, TextInput } from '../../ui/fields';
import { RichTextArea } from '../../ui/RichTextArea';
import { useToast } from '../../ui/Toast';

/**
 * „E-Mail prüfen und senden“ (Plan 06 Abschnitt 5.3): Empfänger, CC, Betreff, Anhänge als Chips (entfernen, aus den
 * Dateien des Datensatzes hinzufügen), Text mit Anrede und Signatur; Fußzeile „Später“, „In Outlook öffnen“, „Senden“.
 * Gesendete Mails werden nur angezeigt.
 */
export function EmailModal({ emailId, entityType, entityId, onClose, onSent, discardIfUntouched }: { emailId: number; entityType: EmailEntityType; entityId: number; onClose: () => void; onSent?: () => void; discardIfUntouched?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['email', emailId], queryFn: () => api.get<EmailDto>(`/emails/${emailId}`) });
  const transport = useQuery({ queryKey: ['mail-transport'], queryFn: () => api.get<MailTransportInfo>('/emails/transport'), staleTime: 60_000 });
  const docs = useQuery({ queryKey: ['attachable', entityType, entityId], queryFn: () => api.get<{ items: { id: number; original_name: string; size_bytes: number | null; mime: string | null; source: string }[] }>(`/emails/attachable${qs({ entity_type: entityType, entity_id: entityId })}`).then((r) => r.items) });
  const [d, setD] = useState<{ to: string; cc: string; subject: string; body: string; attachments: EmailDto['attachments'] } | null>(null);
  const [addDoc, setAddDoc] = useState('');
  const [markConfirm, setMarkConfirm] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  useEffect(() => {
    if (q.data && !d) setD({ to: q.data.to.join(', '), cc: q.data.cc.join(', '), subject: q.data.subject, body: q.data.body, attachments: q.data.attachments });
  }, [q.data, d]);
  const m = q.data;
  const readOnly = m?.status === 'sent';
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['email', emailId] });
    qc.invalidateQueries({ queryKey: ['entity-emails', entityType, entityId] });
    qc.invalidateQueries({ queryKey: ['action', entityId] });
    qc.invalidateQueries({ queryKey: ['action-documents', entityId] });
    qc.invalidateQueries({ queryKey: ['today'] });
    qc.invalidateQueries({ queryKey: ['area-monitorings'] });
    qc.invalidateQueries({ queryKey: ['monitoring', entityId] });
  };
  const split = (s: string) => s.split(/[;,]\s*|\n/).map((x) => x.trim()).filter(Boolean);
  const dirty = !!m && !!d && (d.to !== m.to.join(', ') || d.cc !== m.cc.join(', ') || d.subject !== m.subject || d.body !== m.body || d.attachments.map((a) => a.id).join() !== m.attachments.map((a) => a.id).join());
  const save = useMutation({
    mutationFn: () => api.patch<EmailDto>(`/emails/${emailId}`, { to: split(d!.to), cc: split(d!.cc), subject: d!.subject, body: d!.body, attachments: d!.attachments.map((a) => a.id), updated_at: m!.updated_at }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const send = useMutation({
    mutationFn: async () => {
      if (dirty) await save.mutateAsync();
      return api.post<{ email: EmailDto; applied: string[]; dev: boolean }>(`/emails/${emailId}/send`, {});
    },
    onSuccess: (r) => {
      toast(r.dev ? `Entwicklungsmodus: Mail protokolliert, nicht versendet${r.applied.length ? ' · ' + r.applied.join(' · ') : ''}` : `Gesendet und dokumentiert${r.applied.length ? ' · ' + r.applied.join(' · ') : ''}`);
      invalidate();
      onSent?.();
      onClose();
    },
    onError: (e: Error) => {
      invalidate();
      toast(e instanceof ApiRequestError && e.code === 'graph_consent' ? `${e.message} Zustimmung im Profil erteilen.` : e.message, 'error');
    },
  });
  const markSent = useMutation({
    mutationFn: async () => {
      if (dirty) await save.mutateAsync();
      return api.post<{ email: EmailDto; applied: string[] }>(`/emails/${emailId}/mark-sent`);
    },
    onSuccess: (r) => {
      toast(`Als gesendet vermerkt${r.applied.length ? ' · ' + r.applied.join(' · ') : ''}`);
      invalidate();
      onSent?.();
      onClose();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  /** Entwurf verwerfen (archivieren); gesendete Mails bleiben im Verlauf */
  const discard = useMutation({
    mutationFn: () => api.post(`/emails/${emailId}/archive`),
    onSuccess: () => (invalidate(), onClose()),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const later = async () => {
    if (discardIfUntouched && !dirty && !readOnly && !markConfirm) {
      discard.mutate();
      return;
    }
    if (dirty && !readOnly) {
      try {
        await save.mutateAsync();
        toast('Entwurf gespeichert');
      } catch {
        return;
      }
    }
    onClose();
  };
  const mailto = () => {
    if (!d) return;
    const to = split(d.to).map((a) => parseAddress(a)?.email ?? a).join(',');
    window.open(`mailto:${to}?subject=${encodeURIComponent(d.subject)}&body=${encodeURIComponent(richToText(d.body))}`, '_self');
    setMarkConfirm(true);
  };
  const t = transport.data;
  const transportNote = !t ? null : t.graph_consented ? 'Versand über Microsoft 365 als du selbst, Kopie in Outlook „Gesendet“.' : t.graph_available ? 'Für den Versand über Microsoft 365 fehlt noch deine Zustimmung (Profil). Bis dahin geht der Versand über das Sammelpostfach ohne Kopie in Outlook.' : t.smtp_dev ? 'Entwicklungsmodus: Mails werden nur protokolliert, nicht versendet.' : t.smtp_available ? 'Versand über das Sammelpostfach; keine Kopie in Outlook. Antworten kommen an deine Adresse.' : 'Kein Versandweg eingerichtet. „In Outlook öffnen“ und dort senden, danach hier bestätigen.';
  const canSend = !!t && (t.graph_consented || t.smtp_available);
  const availableDocs = (docs.data ?? []).filter((x) => !d?.attachments.some((a) => a.id === x.id));
  return (
    <Modal
      title={readOnly ? 'Gesendete E-Mail' : 'E-Mail prüfen'}
      subtitle={m ? (m.template_name ?? 'Freie E-Mail') + (m.status === 'sent' ? ` · gesendet ${fmtDateTime(m.sent_at)}${m.sent_by ? ` von ${m.sent_by.initials ?? m.sent_by.display_name}` : ''}${m.provider_label ? ` · ${m.provider_label}` : ''}` : m.status === 'failed' ? ' · Versand fehlgeschlagen' : '') : undefined}
      size="wide"
      onClose={later}
      footer={
        readOnly ? (
          <button className="btn" type="button" onClick={onClose}>
            Schließen
          </button>
        ) : (
          <>
            <button className="btn" type="button" style={{ marginRight: 'auto' }} onClick={() => setConfirmDiscard(true)} disabled={discard.isPending}>
              <Icon name="trash" /> Entwurf verwerfen
            </button>
            <button className="btn" type="button" onClick={later} disabled={save.isPending}>
              Später
            </button>
            <button className="btn" type="button" onClick={mailto} disabled={!d || !split(d.to).length}>
              <Icon name="external" /> In Outlook öffnen
            </button>
            <button className="btn primary" type="button" onClick={() => send.mutate()} disabled={!d || !canSend || send.isPending || !split(d.to).length || !d.subject.trim()} title={canSend ? undefined : 'Kein Versandweg eingerichtet'}>
              <Icon name="mail" /> Senden
            </button>
          </>
        )
      }
    >
      {!m || !d ? (
        <Skeleton rows={6} />
      ) : (
        <div className="form one">
          {m.status === 'failed' && m.error && <Note kind="bad">Letzter Versand fehlgeschlagen: {m.error}</Note>}
          {m.warnings.map((w) => (
            <Note key={w}>{w}</Note>
          ))}
          <TextInput label="An" value={d.to} onChange={(v) => setD({ ...d, to: v })} placeholder="Name <adresse>, weitere durch Komma" disabled={readOnly} hint={readOnly ? undefined : 'Mehrere Empfänger durch Komma trennen'} />
          <TextInput label="CC" value={d.cc} onChange={(v) => setD({ ...d, cc: v })} disabled={readOnly} />
          <TextInput label="Betreff" value={d.subject} onChange={(v) => setD({ ...d, subject: v })} disabled={readOnly} />
          <div className="field full">
            <label>Anhänge</label>
            <div className="chips">
              {d.attachments.length === 0 && <span className="mut small">keine</span>}
              {d.attachments.map((a) => (
                <span key={a.id} className={`chip on ${a.missing ? 'miss' : ''}`}>
                  <Icon name="doc" />
                  <a className="link" href={`/api/v1/documents/${a.id}/file`} target="_blank" rel="noreferrer">
                    {a.original_name}
                  </a>
                  {a.size_bytes != null && <span className="n">{fmtNum(Math.round(a.size_bytes / 1024))} KB</span>}
                  {!readOnly && (
                    <button type="button" className="icon-btn" aria-label={`${a.original_name} entfernen`} onClick={() => setD({ ...d, attachments: d.attachments.filter((x) => x.id !== a.id) })}>
                      <Icon name="x" />
                    </button>
                  )}
                </span>
              ))}
            </div>
            {!readOnly && availableDocs.length > 0 && (
              <div className="form" style={{ marginTop: 8 }}>
                <Select
                  label="Aus den Dateien hinzufügen"
                  value={addDoc}
                  placeholder="Datei wählen"
                  onChange={(v) => {
                    const doc = availableDocs.find((x) => String(x.id) === v);
                    if (doc) setD({ ...d, attachments: [...d.attachments, { id: doc.id, original_name: doc.original_name, size_bytes: doc.size_bytes, mime: doc.mime }] });
                    setAddDoc('');
                  }}
                  options={availableDocs.map((x) => ({ value: String(x.id), label: `${x.original_name} (${x.source})` }))}
                />
              </div>
            )}
          </div>
          <RichTextArea label="Text" value={d.body} onChange={(v) => setD({ ...d, body: v })} rows={14} disabled={readOnly} />
          {!readOnly && transportNote && <Note kind={canSend ? 'info' : 'warn'}>{transportNote}</Note>}
          {markConfirm && !readOnly && (
            <Note kind="info">
              Wenn die E-Mail in Outlook gesendet wurde, hier bestätigen, damit Verlauf und Checkliste stimmen (Anhänge müssen in Outlook von Hand angehängt werden).{' '}
              <button type="button" className="btn sm primary" style={{ marginLeft: 8 }} onClick={() => markSent.mutate()} disabled={markSent.isPending}>
                In Outlook gesendet
              </button>
            </Note>
          )}
        </div>
      )}
      {confirmDiscard && <ConfirmDialog title="Entwurf verwerfen" text={`Entwurf${m?.subject ? ` „${m.subject}“` : ''} verwerfen?`} confirmLabel="Verwerfen" danger onConfirm={() => discard.mutate()} onClose={() => setConfirmDiscard(false)} busy={discard.isPending} />}
    </Modal>
  );
}
